import { useGT } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';

/**
 * Sentinel value for the "Unassigned" option. shadcn's `Select` forbids an
 * empty-string item value, so null is represented by this non-empty token and
 * mapped back to `null` in `onValueChange`.
 */
const UNASSIGNED_VALUE = '__unassigned__';

interface UserSelectProps {
  /** Currently selected org user id, or null when unassigned. */
  value: string | null;
  /** Fired with the new user id, or null when the "Unassigned" option is picked. */
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  /** Render an explicit "Unassigned" option that maps to null. Defaults to true. */
  allowUnassigned?: boolean;
  /** Override the "Unassigned" option label. */
  unassignedLabel?: string;
  placeholder?: string;
  /** id wired to an external <Label htmlFor>. */
  triggerId?: string;
  className?: string;
}

/**
 * Single-select dropdown of active org users, backed by `useUsersInOrg()`.
 * Reused for collection-thread assignment, the AR default-assignee config
 * field, and the create-thread form so the picker behaves identically
 * everywhere (same source list, same "Unassigned" handling).
 */
export function UserSelect({
  value,
  onChange,
  disabled,
  allowUnassigned = true,
  unassignedLabel,
  placeholder,
  triggerId,
  className,
}: UserSelectProps) {
  const gt = useGT();
  const { data: users, isLoading } = useUsersInOrg();

  const selectValue = value ?? (allowUnassigned ? UNASSIGNED_VALUE : undefined);

  return (
    <Select
      value={selectValue}
      onValueChange={(next) =>
        onChange(next === UNASSIGNED_VALUE ? null : next)
      }
      disabled={disabled || isLoading}
    >
      <SelectTrigger id={triggerId} className={className}>
        <SelectValue
          placeholder={
            placeholder ??
            gt('Select a user…', { context: 'placeholder, org user picker' })
          }
        />
      </SelectTrigger>
      <SelectContent>
        {allowUnassigned && (
          <SelectItem value={UNASSIGNED_VALUE}>
            {unassignedLabel ??
              gt('Unassigned', { context: 'option label, no user assigned' })}
          </SelectItem>
        )}
        {(users ?? []).map((user) => (
          <SelectItem key={user.id} value={user.id}>
            {user.email ? `${user.name} (${user.email})` : user.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
